import { useState, useEffect } from "react";
import { Shield, Lock, Eye, FileText, Server, Globe, AlertTriangle, CheckCircle, Clock, User, Activity } from "lucide-react";
import { getToken } from "../lib/auth";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SecurityPage() {
  const [securityInfo, setSecurityInfo] = useState(null);
  const [auditStats, setAuditStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        const headers = { Authorization: `Bearer ${token}` };
        const [secRes, statsRes, logsRes] = await Promise.all([
          fetch(`${API}/api/audit/security-info`),
          token ? fetch(`${API}/api/audit/stats`, { headers }).catch(() => null) : null,
          token ? fetch(`${API}/api/audit/logs?limit=20`, { headers }).catch(() => null) : null,
        ]);
        setSecurityInfo(await secRes.json());
        if (statsRes?.ok) setAuditStats(await statsRes.json());
        if (logsRes?.ok) {
          const data = await logsRes.json();
          setAuditLogs(data.logs || []);
        }
      } catch { }
      setLoading(false);
    };
    fetchData();
  }, []);

  const protections = [
    {
      icon: Lock,
      title: "Chiffrement AES-256",
      desc: "Vos données sensibles (noms, descriptions, informations patients) sont chiffrées avec l'algorithme AES avant d'être stockées en base de données. Même en cas d'accès non autorisé à la base, les données restent illisibles.",
      status: securityInfo?.encryption?.active,
      details: securityInfo?.encryption?.algorithm,
    },
    {
      icon: Globe,
      title: "HTTPS / TLS 1.3",
      desc: "Toutes les communications entre votre navigateur et nos serveurs sont chiffrées via le protocole HTTPS. Aucune donnée ne transite en clair sur Internet.",
      status: true,
      details: "Certificat SSL/TLS actif",
    },
    {
      icon: Shield,
      title: "Authentification Sécurisée",
      desc: "Vos mots de passe sont hachés avec bcrypt (avec sel cryptographique). Personne, pas même l'équipe technique, ne peut lire votre mot de passe. Les sessions expirent automatiquement après 24h.",
      status: true,
      details: securityInfo?.authentication?.method,
    },
    {
      icon: Eye,
      title: "Isolation des Données",
      desc: "Chaque pharmacie accède uniquement à ses propres données. Il est techniquement impossible pour une pharmacie de voir les factures ou les informations d'une autre pharmacie.",
      status: securityInfo?.data_isolation?.active,
      details: "Filtrage par identifiant unique",
    },
    {
      icon: Activity,
      title: "Journalisation des Accès",
      desc: "Chaque consultation de données sensibles est enregistrée : qui a accédé à quoi, quand, et depuis quelle adresse IP. Cette traçabilité permet de détecter toute activité suspecte.",
      status: securityInfo?.audit_logging?.active,
      details: "Piste d'audit complète",
    },
    {
      icon: Server,
      title: "Aucun Partage avec des Tiers",
      desc: "Vos données ne sont jamais partagées, vendues, ou transmises à des tiers. Elles restent exclusivement sur nos serveurs sécurisés et ne sont accessibles que par vous.",
      status: true,
      details: "Engagement contractuel",
    },
    {
      icon: AlertTriangle,
      title: "En-têtes de Sécurité HTTP",
      desc: "Des protections supplémentaires sont activées : Content Security Policy (CSP), protection contre le clickjacking (X-Frame-Options), protection XSS, et plus.",
      status: securityInfo?.headers?.csp,
      details: "CSP, X-Frame-Options, X-XSS-Protection",
    },
    {
      icon: FileText,
      title: "Vos Droits sur vos Données",
      desc: "Conformément aux bonnes pratiques de protection des données, vous pouvez à tout moment demander l'accès, la rectification, ou la suppression de vos données personnelles.",
      status: true,
      details: "Droit d'accès, rectification, suppression",
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="security-page-loading">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="security-page">
      {/* Hero - matching the app's light theme */}
      <div className="border-b border-slate-200 bg-white/80 backdrop-blur">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center shadow-lg shadow-sky-200">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="security-title">Sécurité & Confidentialité</h1>
              <p className="text-sky-600 text-sm mt-0.5 font-medium">La protection de vos données est notre priorité absolue</p>
            </div>
          </div>
          <p className="text-base text-slate-600 max-w-3xl leading-relaxed">
            CMES-LOG a été conçu avec la sécurité au cœur de son architecture. 
            Vos chiffres d'affaires, vos factures et vos données patients <strong className="text-slate-800">ne quittent jamais 
            l'environnement sécurisé</strong>. Voici en toute transparence comment nous protégeons vos informations.
          </p>
        </div>
      </div>

      {/* Protection Features */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-base font-bold text-slate-800 mb-6">Nos 8 niveaux de protection</h2>
        <div className="grid md:grid-cols-2 gap-5" data-testid="security-features-grid">
          {protections.map((p, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:border-sky-200 transition-all" data-testid={`security-feature-${i}`}>
              <div className="flex items-start gap-3.5">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${p.status ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                  <p.icon className="w-4.5 h-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-slate-800 text-sm">{p.title}</h3>
                    {p.status ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded-full text-[10px] font-semibold border border-emerald-100">
                        <CheckCircle className="w-2.5 h-2.5" /> Actif
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full text-[10px] font-semibold border border-amber-100">
                        <Clock className="w-2.5 h-2.5" /> Configuration
                      </span>
                    )}
                  </div>
                  <p className="text-slate-600 text-xs leading-relaxed">{p.desc}</p>
                  {p.details && (
                    <p className="text-[10px] text-slate-400 mt-1.5 font-mono bg-slate-50 inline-block px-2 py-0.5 rounded">{p.details}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Encryption Visual */}
        <div className="mt-10 bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid="encryption-visual">
          <h2 className="text-base font-bold text-slate-800 mb-6 flex items-center gap-2">
            <Lock className="w-5 h-5 text-sky-600" />
            Comment fonctionne le chiffrement ?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-sky-600" />
              </div>
              <h3 className="font-semibold text-slate-800 text-sm mb-2">1. Vos données</h3>
              <div className="bg-slate-50 rounded-lg p-3 text-xs font-mono text-slate-700 border border-slate-100">
                <div>Nom: "NSIA Assurances"</div>
                <div>Patient: "Kouamé Jean"</div>
              </div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-slate-800 text-sm mb-2">2. Chiffrement AES</h3>
              <div className="bg-emerald-50 rounded-lg p-3 text-xs font-mono text-emerald-700 border border-emerald-100">
                <div>ENC:gAAAAABn9...</div>
                <div>ENC:xKmP7qWz...</div>
              </div>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center">
                <Server className="w-5 h-5 text-violet-600" />
              </div>
              <h3 className="font-semibold text-slate-800 text-sm mb-2">3. Stocké en base</h3>
              <div className="bg-violet-50 rounded-lg p-3 text-xs text-violet-600 border border-violet-100">
                Données illisibles sans la clé de déchiffrement
              </div>
            </div>
          </div>
        </div>

        {/* Audit Logs (Admin only) */}
        {auditStats && (
          <div className="mt-10" data-testid="audit-section">
            <h2 className="text-base font-bold text-slate-800 mb-5 flex items-center gap-2">
              <Activity className="w-5 h-5 text-sky-600" />
              Journal des Accès (Audit Trail)
            </h2>
            <div className="grid md:grid-cols-3 gap-4 mb-5">
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide font-semibold">Total événements</p>
                <p className="text-2xl font-bold text-slate-800" data-testid="audit-total">{auditStats.total_events}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide font-semibold">Chiffrement</p>
                <p className={`text-sm font-semibold ${auditStats.encryption_active ? "text-emerald-600" : "text-amber-600"}`}>
                  {auditStats.encryption_active ? "Actif — AES-128-CBC" : "Non configuré"}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <p className="text-[10px] text-slate-500 mb-1 uppercase tracking-wide font-semibold">Utilisateurs suivis</p>
                <p className="text-2xl font-bold text-slate-800">{auditStats.by_user?.length || 0}</p>
              </div>
            </div>

            {auditLogs.length > 0 && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-700">Derniers accès enregistrés</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="audit-logs-table">
                    <thead className="bg-slate-50 text-slate-500 text-xs">
                      <tr>
                        <th className="px-4 py-2.5 text-left font-semibold">Date</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Utilisateur</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Action</th>
                        <th className="px-4 py-2.5 text-left font-semibold">Détails</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {auditLogs.map((log, i) => (
                        <tr key={i} className="hover:bg-sky-50/50 transition-colors">
                          <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">
                            {new Date(log.timestamp).toLocaleString("fr-FR")}
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-1.5">
                              <User className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-700">{log.user_email}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <span className="px-2 py-0.5 bg-sky-50 text-sky-700 rounded text-xs font-medium border border-sky-100">
                              {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-500 max-w-xs truncate">{log.details}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Contact */}
        <div className="mt-10 mb-8 bg-white border border-sky-100 rounded-xl p-7 text-center shadow-sm" data-testid="security-contact">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br from-sky-400 to-emerald-400 flex items-center justify-center shadow-md shadow-sky-200">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-base font-bold text-slate-800 mb-2">Une question sur la sécurité ?</h2>
          <p className="text-sm text-slate-600 max-w-lg mx-auto mb-3">
            Notre équipe technique est à votre disposition pour répondre à toutes vos questions
            concernant la protection de vos données.
          </p>
          <p className="text-sm font-bold text-sky-700">contact@cmes-pharma.org | 07 68 12 70 00</p>
        </div>
      </div>
    </div>
  );
}
